import { useEffect, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { createPlanningEntry, createBulkPlanningEntries, deletePlanningEntry, getPlanningEntries } from "../../../../services/planning";
import { t } from "i18next";
import { ColorSelector } from "../../../general/color-selector";
import { TrainingSessionTpl } from "../../../../interfaces/training-session-tpl";
import { getTrainingSessionTplOptions } from "../../../../services/training-session-tpl";
import { PlanningEntry } from "../../../../interfaces/planning";

interface PlanningEntriesProps {
    athlete_id: number;
    day: string;
    selectedDays?: {athlete_id: number, day: string}[];
    onSave: CallableFunction;
    onClose: (success?: boolean, count?: number) => void;
}

export function PlanningEntries(props: PlanningEntriesProps) {
    const [loaded, setLoaded] = useState(false);
    const [planningEntries, setPlanningEntries] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [color, setColor] = useState('#34B689');
    const [templates, setTemplates] = useState<TrainingSessionTpl[]>([]);

    useEffect(() => {
        getPlanningEntries(props.athlete_id, props.day, props.day)
            .then(response => {
                setPlanningEntries(response.data);
                setLoaded(true);
            });
        getTrainingSessionTplOptions()
            .then((response: any) => {
                setTemplates(response.data.map((t: TrainingSessionTpl) => {
                    return {
                        value: t.id,
                        label: t.name
                    }
                }));
            });
    }, []);

    const handleClose = () => {
        props.onClose();
    }

    const handleSave = (event: any) => {
        event.preventDefault();

        const description = event.target.description.value;
        const template_id = event.target.template_id.value;

        // Si hay días seleccionados, crear entradas para todos ellos usando el endpoint bulk
        if (props.selectedDays && props.selectedDays.length > 0) {
            const entries = props.selectedDays.map(dayInfo => ({
                athlete_id: dayInfo.athlete_id,
                day: dayInfo.day,
                description: description,
                color: color,
                training_session_tpl_id: template_id
            }));

            createBulkPlanningEntries(entries)
                .then((response: any) => {
                    if (response.data.status == 1) {
                        const createdCount = response.data.created_count || entries.length;
                        props.onClose(true, createdCount);
                    } else {
                        alert(response.data.message || t("Some entries could not be created"));
                        props.onClose(false);
                    }
                })
                .catch((error: any) => {
                    console.log(error);
                    alert(t("Error creating entries"));
                    props.onClose(false);
                });
        } else {
            // Modo normal: crear entrada para un solo día
            const body = {
                athlete_id: event.target.athlete_id.value,
                day: event.target.day.value,
                description: description,
                color: color,
                training_session_tpl_id: template_id
            }
            createPlanningEntry(body)
                .then((response: any) => {
                    if (response.data.status == 1) {
                        getPlanningEntries(props.athlete_id, props.day, props.day).then(response => {
                            setPlanningEntries(response.data);
                            setLoaded(true);
                            setShowAdd(false);
                        });
                    } else {
                        alert(response.data.message);
                    }
                })
                .catch((error: any) => {
                    console.log(error);
                });
        }
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        const id = event.currentTarget.dataset.id;
        deletePlanningEntry(event.currentTarget.dataset.id)
            .then(response => {
                if (response.data == 0) {
                    alert(t("Training cannot be deleted. Try again later."));
                } else {
                    const newPlanningEntries = planningEntries.filter((pe: PlanningEntry) => pe.id != id);
                    setPlanningEntries(newPlanningEntries);
                }
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAdd(true);
    }

    const handleCloseAddNew = (event: any) => {
        event.preventDefault();
        setShowAdd(false);
    }


    const handleSetColor = (color: string) => {
        setColor(color);
    }
    
    const isBulkMode = props.selectedDays && props.selectedDays.length > 0;

    return (
        <Modal className="planning-entries-container" show={ true }
            onHide={ handleClose } size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    { isBulkMode ? t("Add training to multiple days") : t("Athlete trainings") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { isBulkMode && (
                    <div className="bulk-mode-info" style={{
                        backgroundColor: '#e7f3ff',
                        border: '1px solid #b3d9ff',
                        borderRadius: '4px',
                        padding: '15px',
                        marginBottom: '20px'
                    }}>
                        <p style={{ margin: 0, fontWeight: 'bold', color: '#004085' }}>
                            <i className="bi bi-info-circle"></i> {t("You are adding training to")} <strong>{props.selectedDays?.length || 0}</strong> {t("selected days")}
                        </p>
                    </div>
                )}
                { !loaded && !isBulkMode &&
                    <LoadingSpinner />
                }
                { loaded && !isBulkMode &&
                    <div className="athlete-planning-entries-container">
                        <div className="subtable-container">
                            <Table striped bordered hover responsive className="planning-entries-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{ t('Session') }</th>
                                        <th>{ t('description') }</th>
                                        <th>{ t('color') }</th>
                                        <th>{ t('Template') }</th>
                                        <th>{ t('actions') }</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    { planningEntries.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-center">
                                                { t('No results found') }
                                            </td>
                                        </tr>
                                    ) : (
                                        planningEntries.map((entry: PlanningEntry) => (
                                            <tr key={entry.id}>
                                                <td>{entry.id}</td>
                                                <td>{entry.session}</td>
                                                <td>{entry.description}</td>
                                                <td>
                                                    <span className="color-cell" style={{backgroundColor: entry.color}}></span>
                                                </td>
                                                <td>{entry.training_session_tpl_name || '-'}</td>
                                                <td>
                                                    <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ entry.id }>
                                                        <i className="bi bi-trash"></i>
                                                    </a>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        { !showAdd &&
                            <a className="measurement-add-new" href="#" onClick={ handleAddNew }>
                                <i className="bi bi-plus-circle"></i>
                            </a>
                        }
                        { showAdd &&
                            <Form onSubmit={ handleSave } className="add-measurement">
                                <a href="#" className="close" onClick={ handleCloseAddNew }>
                                </a>
                                <div className="fields">
                                    <Form.Control type="hidden" name="athlete_id" required defaultValue={ props.athlete_id } />
                                    <Form.Control type="hidden" name="day" required defaultValue={ props.day } />
                                    <Form.Group className="mb-3" controlId="description">
                                        <Form.Label>{ t("description") }</Form.Label>
                                        <Form.Control name="description" as="textarea" rows={5} required />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="color">
                                        <Form.Label>{ t("color") }</Form.Label>
                                        <ColorSelector setColor={ handleSetColor } defaultColor={ color } />
                                    </Form.Group>
                                    <Form.Select name="template_id" aria-label={ t("template")} className="mt-3">
                                        <option value="0">{ t("select_template") }</option>
                                        { templates.map((t: any) => {
                                            return <option key={ 'template-' + t.value } value={ t.value }>{ t.label }</option>
                                        })}
                                    </Form.Select>
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">
                                        { t("Save") }
                                    </Button>
                                    {/*<Button variant="secondary" onClick={ handleCloseAddNew }>
                                        { t("Close") }
                                    </Button>*/}
                                </div>
                            </Form>
                        }
                    </div>
                }
                { isBulkMode &&
                    <div className="athlete-planning-entries-container">
                        <Form onSubmit={ handleSave } className="add-measurement">
                            <div className="fields">
                                <Form.Group className="mb-3" controlId="description">
                                    <Form.Label>{ t("description") }</Form.Label>
                                    <Form.Control name="description" as="textarea" rows={5} required />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="color">
                                    <Form.Label>{ t("color") }</Form.Label>
                                    <ColorSelector setColor={ handleSetColor } defaultColor={ color } />
                                </Form.Group>
                                <Form.Select name="template_id" aria-label={ t("template")} className="mt-3">
                                    <option value="0">{ t("select_template") }</option>
                                    { templates.map((t: any) => {
                                        return <option key={ 'template-' + t.value } value={ t.value }>{ t.label }</option>
                                    })}
                                </Form.Select>
                            </div>
                            <div className="actions">
                                <Button type="submit" variant="primary">
                                    <i className="bi bi-check-circle"></i> { t("Add to all selected days") }
                                </Button>
                            </div>
                        </Form>
                    </div>
                }
            </Modal.Body>
        </Modal>
    );
}